import React, { useState, useEffect } from 'react';
import { Typography, Box, IconButton, Tooltip as MuiTooltip, Button, Modal, TextField, Select, MenuItem, FormControl, InputLabel, Popover } from '@mui/material';
import awsLogo from '../assets/Website/AWS.svg';
import googleLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import azureLogo from '../assets/Website/Microsoft_Azure.svg';
import anthropicLogo from '../assets/cloud-providers/anthropic-logo.svg';
import googleCloudLogo from '../assets/cloud-providers/google-cloud-icon.svg';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useAuth, useSelectedWorkspace } from '../contexts/AuthContext';
import { TabIndex } from '../navigation';
import {
  Cell,
  ResponsiveContainer,
  Tooltip,
  PieChart,
  Pie,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
} from 'recharts';
import { useApi } from '../contexts/ApiContext';
import { useWithTokenRefresh } from '../utils/api';
import { ApiTypes } from '@flashbacktech/flashbackclient/api';
import { buildTimeSeriesParams, buildBreakdownParams } from '../utils/v2StatsHelpers';

interface RepoStat {
  repoId: string;
  requests: number;
  uplBytes: number;
  dwlBytes: number;
}


interface TransferDataItem {
  name: string;
  value: number;
  valueInGB?: number;
  color: string;
  uploadedBytes?: number;
  downloadedBytes?: number;
}

const RepoOverview: React.FC = () => {
  const { authState, globalData } = useAuth();
  const navigate = useNavigate();
  const { apiClient } = useApi();
  const withTokenRefresh = useWithTokenRefresh();

  // Custom tooltip for pie chart - shows only requests count
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-2 border border-gray-200 shadow-sm text-sm rounded">
          <p className="font-semibold">{data.name}</p>
          <p>{`Requests: ${formatNumber(data.value)}`}</p>
        </div>
      );
    }
    return null;
  };

  // Helper function to navigate to a tab using custom events
  const navigateToTab = (tabIndex: number, path: string) => {
    const rootEl = document.getElementById('root');
    if (rootEl) {
      rootEl.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
    }
    window.dispatchEvent(new CustomEvent('navigateToTab', { detail: { tabIndex } }));
    navigate(path);
  };
  // Real data from API
  const [repos, setRepos] = useState<ApiTypes.StorageRepoWithBuckets[]>([]);
  const [repoStats, setRepoStats] = useState<Record<string, RepoStat>>({});
  
  // AI stats for tokens
  const [totalTokensIn, setTotalTokensIn] = useState<number>(0);
  const [totalTokensOut, setTotalTokensOut] = useState<number>(0);
  
  // Per-repo AI token stats
  const [repoTokenStats, setRepoTokenStats] = useState<Record<string, { tokensIn: number; tokensOut: number }>>({});
  
  // Modal state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [newRepoName, setNewRepoName] = useState('');
  const [newRepoProvider, setNewRepoProvider] = useState('S3');
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  const [transferData, setTransferData] = useState<TransferDataItem[]>([]);
  
  // Request rate chart data (30 days)
  const [requestRateData, setRequestRateData] = useState<Array<{ date: string; requests: number }>>([]);
  
  // Use centralized workspace hook
  const selectedWorkspaceId = useSelectedWorkspace();
  
  // Get user first name
  const firstName = globalData?.userProfile?.name || authState?.user?.name || 'User';

  // Load repos and stats
  useEffect(() => {
    loadRepos();
  }, [selectedWorkspaceId]);

  // Recalculate top repos when data changes
  useEffect(() => {
    if (repos.length > 0 && Object.keys(repoStats).length > 0) {
      calculateTopRepos(repoStats);
    }
  }, [repos, repoStats]);

  const loadRepos = async () => {
    if (!authState?.token) return;
    
    setIsLoading(true);
    try {
      const response = await withTokenRefresh(() => apiClient.getStorageRepos(selectedWorkspaceId));
      
      if (response.success && response.repos) {
        setRepos(response.repos);
        
        // Load stats for repos if any
        if (response.repos.length > 0) {
          await loadProviderStats(response.repos);
          await loadDailyRequestStats();
        }
      } else {
        // No repos found - show empty state
        setRepos([]);
        setRepoStats({});
      }
    } catch (error) {
      console.error('[RepoOverview] Error loading repos:', error);
      // On error - show empty state
      setRepos([]);
      setRepoStats({});
    } finally {
      setIsLoading(false);
      setInitialLoadComplete(true);
    }
  };

  const loadProviderStats = async (reposList: ApiTypes.StorageRepoWithBuckets[]) => {
    if (!authState?.token || reposList.length === 0) return;

    try {
      const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
      const bdParams = buildBreakdownParams(wsId, null, '30d');

      const [storageRes, aiRes] = await Promise.all([
        withTokenRefresh(() => apiClient.getStorageGatewayBreakdown('repos', bdParams as any)).catch(() => null),
        withTokenRefresh(() => apiClient.getAiGatewayBreakdown('repos', bdParams as any)).catch(() => null),
      ]);

      const statsMap: Record<string, RepoStat> = {};
      const perRepoTokens: Record<string, { tokensIn: number; tokensOut: number }> = {};
      let totalIn = 0;
      let totalOut = 0;

      reposList.forEach((repo) => {
        statsMap[repo.id] = { repoId: repo.id, requests: 0, uplBytes: 0, dwlBytes: 0 };
        perRepoTokens[repo.id] = { tokensIn: 0, tokensOut: 0 };
      });

      if ((storageRes as any)?.success && (storageRes as any)?.entities) {
        (storageRes as any).entities.forEach((entity: any) => {
          if (statsMap[entity.entityId]) {
            statsMap[entity.entityId].requests += entity.totals?.requests || 0;
            statsMap[entity.entityId].uplBytes = entity.totals?.uplBytes || 0;
            statsMap[entity.entityId].dwlBytes = entity.totals?.dwlBytes || 0;
          }
        });
      }

      if ((aiRes as any)?.success && (aiRes as any)?.entities) {
        (aiRes as any).entities.forEach((entity: any) => {
          if (statsMap[entity.entityId]) {
            statsMap[entity.entityId].requests += entity.totals?.requests || 0;
          }
          const tIn = entity.totals?.tokensIn || 0;
          const tOut = entity.totals?.tokensOut || 0;
          totalIn += tIn;
          totalOut += tOut;
          if (perRepoTokens[entity.entityId]) {
            perRepoTokens[entity.entityId].tokensIn += tIn;
            perRepoTokens[entity.entityId].tokensOut += tOut;
          }
        });
      }

      setRepoStats(statsMap);
      setTotalTokensIn(totalIn);
      setTotalTokensOut(totalOut);
      setRepoTokenStats(perRepoTokens);
    } catch (error) {
      console.error('[RepoOverview] Error loading provider stats:', error);
      setTotalTokensIn(0);
      setTotalTokensOut(0);
      setRepoTokenStats({});
    }
  };

  const loadDailyRequestStats = async () => {
    if (!authState?.token) {
      setRequestRateData([]);
      return;
    }

    try {
      const wsId = selectedWorkspaceId && selectedWorkspaceId !== 'ALL' ? selectedWorkspaceId : null;
      const params = buildTimeSeriesParams(wsId, null, '30d');

      const [storageRes, aiRes] = await Promise.all([
        withTokenRefresh(() => apiClient.getStorageGatewayStats(params as any)).catch(() => null),
        withTokenRefresh(() => apiClient.getAiGatewayStats(params as any)).catch(() => null),
      ]);

      const dailyMap: Record<string, number> = {};

      const aggregateByDay = (series: any[]) => {
        series.forEach((p: any) => {
          const dateKey = new Date(p.ts * 1000).toISOString().split('T')[0];
          dailyMap[dateKey] = (dailyMap[dateKey] || 0) + (p.requests || 0);
        });
      };

      if ((storageRes as any)?.success && (storageRes as any)?.series) {
        aggregateByDay((storageRes as any).series);
      }
      if ((aiRes as any)?.success && (aiRes as any)?.series) {
        aggregateByDay((aiRes as any).series);
      }

      const chartData = Object.entries(dailyMap)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([dateKey, requests]) => {
          const d = new Date(dateKey);
          const day = String(d.getDate()).padStart(2, '0');
          const month = String(d.getMonth() + 1).padStart(2, '0');
          return { date: `${month}/${day}`, requests };
        });

      setRequestRateData(chartData);
    } catch (error) {
      console.error('[RepoOverview] Error loading daily request stats:', error);
      setRequestRateData([]);
    }
  };

  const getRequestCount = (stat: RepoStat | undefined): number => stat?.requests ?? 0;
  const getUploadBytes = (stat: RepoStat | undefined): number => stat?.uplBytes ?? 0;
  const getDownloadBytes = (stat: RepoStat | undefined): number => stat?.dwlBytes ?? 0;

  const calculateTopRepos = (stats: Record<string, RepoStat>) => {
    if (!repos.length) {
      return;
    }

    console.log('[RepoOverview] ========================================');
    console.log('[RepoOverview] calculateTopRepos called with stats:', stats);
    console.log('[RepoOverview] repos:', repos.map(r => ({ id: r.id, name: r.name })));

    // Sort by requests count (totalCount) instead of bytes
    const reposRequestData = repos
      .filter((repo) => stats[repo.id])
      .map((repo) => {
        const stat = stats[repo.id];
        const requestCount = getRequestCount(stat);
        const uploadBytes = getUploadBytes(stat);
        const downloadBytes = getDownloadBytes(stat);

        console.log(`[RepoOverview] Repo ${repo.name}: requests=${requestCount}, upload=${uploadBytes}, download=${downloadBytes}`);

        return {
          repo,
          requestCount,
          uploadBytes,
          downloadBytes,
        };
      })
      .filter((item) => item.requestCount > 0)
      .sort((a, b) => b.requestCount - a.requestCount); // Sort by requests, not bytes

    console.log('[RepoOverview] Sorted repos by requests:', reposRequestData.map(r => ({ name: r.repo.name, requests: r.requestCount })));

    if (reposRequestData.length === 0) {
      console.log('[RepoOverview] No repos with requests, showing empty state');
      setTransferData([{ name: 'No data', value: 1, valueInGB: 0, color: '#E5E7EB' }]);
      return;
    }

    const top5 = reposRequestData.slice(0, 5);
    const others = reposRequestData.slice(5);

    const colors = ['#617BFF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
    
    // Use requestCount as value for pie chart
    const chartData: TransferDataItem[] = top5.map((item, index) => ({
      name: item.repo.name,
      value: item.requestCount, // Changed from totalBytes to requestCount
      valueInGB: 0,
      color: colors[index] || '#94A3B8',
      uploadedBytes: item.uploadBytes,
      downloadedBytes: item.downloadBytes,
    }));

    if (others.length > 0) {
      const othersRequests = others.reduce((sum, item) => sum + item.requestCount, 0);
      const othersUpload = others.reduce((sum, item) => sum + item.uploadBytes, 0);
      const othersDownload = others.reduce((sum, item) => sum + item.downloadBytes, 0);
      chartData.push({
        name: `Others (${others.length})`,
        value: othersRequests,
        valueInGB: 0,
        color: '#94A3B8',
        uploadedBytes: othersUpload,
        downloadedBytes: othersDownload,
      });
    }

    console.log('[RepoOverview] Final chartData for pie:', chartData);
    console.log('[RepoOverview] ========================================');
    setTransferData(chartData);
  };

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    }
    if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const totalRequests = Object.values(repoStats).reduce((sum: number, stat: RepoStat) => {
    return sum + getRequestCount(stat);
  }, 0);

  const totalUpload = Object.values(repoStats).reduce((sum: number, stat: RepoStat) => {
    return sum + getUploadBytes(stat);
  }, 0);

  const totalDownload = Object.values(repoStats).reduce((sum: number, stat: RepoStat) => {
    return sum + getDownloadBytes(stat);
  }, 0);

  // Global loader during initial data fetch
  if (isLoading && !initialLoadComplete) {
    return (
      <div
        className="flex flex-col items-center justify-center"
        style={{ minHeight: 'calc(100vh - 200px)' }}
      >
        <div
          className="animate-spin rounded-full h-16 w-16 border-b-2 mb-4"
          style={{ borderColor: '#617BFF' }}
        ></div>
        <p className="text-gray-600 text-lg font-medium">Loading repository statistics...</p>
        <p className="text-gray-500 text-sm mt-2">Fetching data and metrics</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Header */}
      <div style={{ padding: '24px 40px 24px 40px' }}>
        <Typography
          style={{
            fontSize: '32px',
            fontWeight: 700,
            color: '#1F2937',
            marginBottom: '8px',
            fontFamily: 'Inter',
            letterSpacing: '-0.02em',
          }}
        >
          Repositories
        </Typography>
        <Typography
          style={{
            fontSize: '16px',
            fontWeight: 400,
            color: '#6B7280',
            fontFamily: 'Inter',
            lineHeight: '24px',
          }}
        >
          Manage your storage repositories and their configurations
        </Typography>
      </div>

      {/* Content */}
      <div className="flex-1 px-10 pb-6 overflow-y-auto">

      {/* Overview Sub-section Header with buttons */}
      <Box sx={{ ml: '8px' }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#101828', mb: 0.5 }}>
              Overview
            </Typography>
            <Typography sx={{ fontSize: '14px', color: '#667085' }}>
              Welcome back, {firstName}. You have {repos.length} repositories with {formatNumber(totalRequests)} requests.
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {/* Reload */}
            <MuiTooltip title="Reload">
              <IconButton
                size="small"
                onClick={() => loadRepos()}
                sx={{ width: 36, height: 36, border: '1px solid #D0D5DD', borderRadius: '8px', color: '#667085', '&:hover': { bgcolor: '#F9FAFB', borderColor: '#98A2B3' } }}
              >
                <RefreshIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </MuiTooltip>
            {/* Add Repository */}
            <MuiTooltip title="Add">
              <IconButton
                size="small"
                onClick={() => navigateToTab(TabIndex.StorageRepos, '/repositories/inventory/add')}
                sx={{ width: 36, height: 36, bgcolor: '#617BFF', borderRadius: '8px', color: 'white', '&:hover': { bgcolor: '#4A67E3' } }}
              >
                <AddIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </MuiTooltip>
          </Box>
        </Box>
      </Box>

      <div className="max-w-7xl mx-auto">
        {/* First Row - 4 Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Total Repositories Card */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#F0F2FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#617BFF" strokeWidth="2">
                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Repositories</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{repos.length}</Typography>
          </div>

          {/* Data Transit Card (30D) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2">
                  <polyline points="17 1 21 5 17 9"/>
                  <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
                  <polyline points="7 23 3 19 7 15"/>
                  <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Data Transit (30D)</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{formatBytes(totalUpload + totalDownload)}</Typography>
          </div>

          {/* Tokens Card (30D) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#ECFDF5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2">
                  <polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/>
                  <path d="M5.45 5.11L2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Tokens (30D)</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{formatNumber(totalTokensIn + totalTokensOut)}</Typography>
          </div>

          {/* Requests Card (30D) */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center gap-3 mb-3">
              <div style={{ width: 40, height: 40, borderRadius: 8, backgroundColor: '#FEF3C7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#F59E0B" strokeWidth="2">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                </svg>
              </div>
              <Typography sx={{ fontSize: '14px', color: '#717680', fontWeight: 500 }}>Requests (30D)</Typography>
            </div>
            <Typography sx={{ fontSize: '28px', fontWeight: 700, color: '#181D27' }}>{formatNumber(totalRequests)}</Typography>
          </div>
        </div>

        {/* Repository Activity Card - Full width like Cloud Storage and AI Overview */}
        <div className="flex flex-col gap-6 mb-8">
          <div className="relative" style={{ width: '100%' }}>
            {/* Gray background card */}
            <div className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200" style={{ height: '518px', position: 'absolute', zIndex: 1 }}></div>
            
            {/* Header */}
            <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: '16px', paddingRight: '16px', position: 'relative', zIndex: 2 }}>
              <Typography variant="subtitle1" style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px', margin: 0 }}>
                Repository Activity
              </Typography>
              <Typography style={{ color: '#535862', fontWeight: 400, fontSize: '13px', lineHeight: '20px' }}>
                Requests: {formatNumber(totalRequests)}
              </Typography>
            </div>

            {/* White card - Split layout: Pie chart left, Repository list right */}
            <div className="w-full bg-white border border-gray-200 shadow-sm flex rounded-xl" style={{ height: '476px', marginTop: '2px', position: 'relative', zIndex: 2 }}>
              {/* Left side - Pie Chart */}
              <div style={{ width: '420px', minWidth: '420px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRight: '1px solid #e5e7eb', padding: '16px' }}>
                {isLoading ? (
                  <div className="flex flex-col items-center justify-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 mb-3" style={{ borderColor: '#617BFF' }}></div>
                    <p className="text-gray-600 text-sm font-medium">Loading data...</p>
                  </div>
                ) : transferData.length === 0 || (transferData.length === 1 && transferData[0].name === 'No data') ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, border: '1px solid #D5D7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12, background: '#FFFFFF' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>
                    <div style={{ color: '#181D27', fontWeight: 600, fontSize: '16px', textAlign: 'center', marginBottom: 4 }}>
                      No data
                    </div>
                    <div style={{ color: '#535862', fontWeight: 400, fontSize: '14px', textAlign: 'center' }}>
                      Add repositories to see usage
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ width: '200px', height: '200px' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={transferData}
                            cx="50%"
                            cy="50%"
                            innerRadius={0}
                            outerRadius={95}
                            paddingAngle={1}
                            strokeWidth={0}
                            dataKey="value"
                            nameKey="name"
                            label={false}
                          >
                            {transferData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    {/* Legend below pie chart */}
                    <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '12px', marginTop: '16px', maxWidth: '280px' }}>
                      {transferData.map((entry, index) => (
                        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <div style={{ width: 10, height: 10, backgroundColor: entry.color, borderRadius: '2px', flexShrink: 0 }}></div>
                          <Typography style={{ fontSize: '12px', color: '#667085', whiteSpace: 'nowrap' }}>{entry.name}</Typography>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Right side - Repository List (Top 5 by requests) */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
                {isLoading ? (
                  <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 mb-3" style={{ borderColor: '#617BFF' }}></div>
                    <p className="text-gray-600 text-sm font-medium">Loading...</p>
                  </div>
                ) : repos.length === 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                    <div style={{ width: 48, height: 48, borderRadius: 10, border: '1px solid #D5D7DA', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 12 }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2">
                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>
                    <div style={{ color: '#181D27', fontWeight: 600, fontSize: '16px', textAlign: 'center', marginBottom: 4 }}>No repositories</div>
                    <div style={{ color: '#535862', fontWeight: 400, fontSize: '14px', textAlign: 'center' }}>Add repositories to see activity</div>
                  </div>
                ) : (
                  <>
                    {/* Header Row - Same style as Storage/AI */}
                    <div
                      style={{
                        height: '40px',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        borderBottom: '1px solid #e5e7eb',
                        borderTopRightRadius: '12px',
                      }}
                    >
                      <div style={{ flex: 1, paddingLeft: '16px' }}>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textTransform: 'none',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Name
                        </Typography>
                      </div>
                      <div style={{ width: '100px', textAlign: 'center' }}>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textTransform: 'none',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Providers
                        </Typography>
                      </div>
                      <div style={{ width: '80px', textAlign: 'center' }}>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textTransform: 'none',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Data Transit
                        </Typography>
                      </div>
                      <div style={{ width: '70px', textAlign: 'center' }}>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textTransform: 'none',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Tokens
                        </Typography>
                      </div>
                      <div style={{ width: '70px', textAlign: 'center' }}>
                        <Typography
                          variant="caption"
                          style={{
                            color: '#6B7280',
                            fontWeight: 600,
                            fontSize: '12px',
                            lineHeight: '18px',
                            textTransform: 'none',
                            letterSpacing: '0.05em',
                          }}
                        >
                          Requests
                        </Typography>
                      </div>
                      <div style={{ width: '40px' }}></div>
                    </div>
                    {/* Repository rows */}
                    {repos
                      .map((repo) => ({ repo, stat: repoStats[repo.id] }))
                      .sort((a, b) => (getRequestCount(b.stat) - getRequestCount(a.stat)))
                      .slice(0, 5)
                      .map(({ repo, stat }, index) => {
                        const colors = ['#617BFF', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];
                        
                        // Collect all providers: storage (from buckets) + AI (from aiLlms)
                        const allProviders: { type: 'storage' | 'ai'; provider: string }[] = [];
                        
                        // Storage providers from buckets
                        const buckets = repo.buckets || [];
                        const storageProviders = [...new Set(buckets.map((b: any) => b.storageType).filter(Boolean))];
                        storageProviders.forEach((p: any) => allProviders.push({ type: 'storage', provider: p }));
                        
                        // AI providers from aiLlms
                        const aiLlms = (repo as any).aiLlms || [];
                        const aiProviders = [...new Set(aiLlms.map((llm: any) => (llm.aiLlm?.aiType || llm.aiLlm?.provider || llm.aiType || llm.provider || '').toUpperCase()).filter(Boolean))];
                        aiProviders.forEach((p: any) => allProviders.push({ type: 'ai', provider: p }));
                        
                        const displayProviders = allProviders.slice(0, 4);
                        const extraCount = allProviders.length - 4;
                        
                        // Get token count for this repo (from AI LLMs)
                        const repoTokenData = repoTokenStats[repo.id] || { tokensIn: 0, tokensOut: 0 };
                        const repoTokens = repoTokenData.tokensIn + repoTokenData.tokensOut;
                        
                        return (
                          <div
                            key={repo.id || index}
                            style={{
                              height: '76px',
                              display: 'flex',
                              alignItems: 'center',
                              backgroundColor: '#FFFFFF',
                              borderBottom: '1px solid #F0F0F0',
                              cursor: 'pointer',
                            }}
                            onClick={() => navigate(`/repositories/inventory/${repo.id}`)}
                          >
                            {/* Name */}
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', paddingLeft: '16px', overflow: 'hidden' }}>
                              <Typography style={{ fontSize: '14px', fontWeight: 500, color: '#181D27', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{repo.name}</Typography>
                            </div>
                            {/* Providers column - Overlapping round logos */}
                            <div style={{ width: '100px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                {displayProviders.map((item, pIndex) => {
                                  const getProviderLogo = () => {
                                    if (item.type === 'storage') {
                                      if (item.provider === ApiTypes.StorageType.S3) return awsLogo;
                                      if (item.provider === ApiTypes.StorageType.GCS) return googleLogo;
                                      if (item.provider === ApiTypes.StorageType.BLOB) return azureLogo;
                                    }
                                    if (item.type === 'ai') {
                                      if (item.provider === 'ANTHROPIC') return anthropicLogo;
                                      if (item.provider === 'GOOGLE' || item.provider === 'GEMINI') return googleCloudLogo;
                                      if (item.provider === 'AWS' || item.provider === 'BEDROCK') return awsLogo;
                                    }
                                    return null;
                                  };
                                  const logo = getProviderLogo();
                                  const isOpenAI = item.type === 'ai' && item.provider === 'OPENAI';
                                  
                                  return (
                                    <div
                                      key={pIndex}
                                      style={{
                                        width: 28,
                                        height: 28,
                                        borderRadius: '50%',
                                        backgroundColor: '#FFFFFF',
                                        border: '2px solid #FFFFFF',
                                        boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        marginLeft: pIndex > 0 ? '-8px' : 0,
                                        zIndex: displayProviders.length - pIndex,
                                        position: 'relative',
                                      }}
                                    >
                                      {isOpenAI ? (
                                        <svg viewBox="0 0 24 24" style={{ width: 16, height: 16 }} fill="#10A37F">
                                          <path d="M22.2819 9.8211a5.9847 5.9847 0 0 0-.5157-4.9108 6.0462 6.0462 0 0 0-6.5098-2.9A6.0651 6.0651 0 0 0 4.9807 4.1818a5.9847 5.9847 0 0 0-3.9977 2.9 6.0462 6.0462 0 0 0 .7427 7.0966 5.98 5.98 0 0 0 .511 4.9107 6.051 6.051 0 0 0 6.5146 2.9001A5.9847 5.9847 0 0 0 13.2599 24a6.0557 6.0557 0 0 0 5.7718-4.2058 5.9894 5.9894 0 0 0 3.9977-2.9001 6.0557 6.0557 0 0 0-.7475-7.0729zm-9.022 12.6081a4.4755 4.4755 0 0 1-2.8764-1.0408l.1419-.0804 4.7783-2.7582a.7948.7948 0 0 0 .3927-.6813v-6.7369l2.02 1.1686a.071.071 0 0 1 .038.052v5.5826a4.504 4.504 0 0 1-4.4945 4.4944zm-9.6607-4.1254a4.4708 4.4708 0 0 1-.5346-3.0137l.142.0852 4.783 2.7582a.7712.7712 0 0 0 .7806 0l5.8428-3.3685v2.3324a.0804.0804 0 0 1-.0332.0615L9.74 19.9502a4.4992 4.4992 0 0 1-6.1408-1.6464zM2.3408 7.8956a4.485 4.485 0 0 1 2.3655-1.9728V11.6a.7664.7664 0 0 0 .3879.6765l5.8144 3.3543-2.0201 1.1685a.0757.0757 0 0 1-.071 0l-4.8303-2.7865A4.504 4.504 0 0 1 2.3408 7.8956zm16.5963 3.8558L13.1038 8.364l2.0201-1.1638a.0757.0757 0 0 1 .071 0l4.8303 2.7913a4.4944 4.4944 0 0 1-.6765 8.1042v-5.6772a.79.79 0 0 0-.4046-.6813zm2.0107-3.0231l-.142-.0852-4.7735-2.7818a.7759.7759 0 0 0-.7854 0L9.409 9.2297V6.8974a.0662.0662 0 0 1 .0284-.0615l4.8303-2.7866a4.4992 4.4992 0 0 1 6.6802 4.66zM8.3065 12.863l-2.02-1.1638a.0804.0804 0 0 1-.038-.0567V6.0742a4.4992 4.4992 0 0 1 7.3757-3.4537l-.142.0805L8.704 5.459a.7948.7948 0 0 0-.3927.6813zm1.0976-2.3654l2.602-1.4998 2.6069 1.4998v2.9994l-2.5974 1.4997-2.6067-1.4997Z"/>
                                        </svg>
                                      ) : logo ? (
                                        <img src={logo} alt="" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                                      ) : null}
                                    </div>
                                  );
                                })}
                                {extraCount > 0 && (
                                  <div
                                    style={{
                                      width: 28,
                                      height: 28,
                                      borderRadius: '50%',
                                      backgroundColor: '#F3F4F6',
                                      border: '2px solid #FFFFFF',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      marginLeft: '-8px',
                                      zIndex: 0,
                                    }}
                                  >
                                    <Typography style={{ fontSize: '10px', fontWeight: 600, color: '#6B7280' }}>+{extraCount}</Typography>
                                  </div>
                                )}
                                {allProviders.length === 0 && (
                                  <Typography style={{ fontSize: '12px', color: '#9CA3AF' }}>-</Typography>
                                )}
                              </div>
                            </div>
                            {/* Data Transit */}
                            <div style={{ width: '80px', textAlign: 'center' }}>
                              <Typography style={{ fontSize: '13px', fontWeight: 400, color: '#667085' }}>
                                {formatBytes(getUploadBytes(stat) + getDownloadBytes(stat))}
                              </Typography>
                            </div>
                            {/* Tokens */}
                            <div style={{ width: '70px', textAlign: 'center' }}>
                              <Typography style={{ fontSize: '13px', fontWeight: 400, color: '#667085' }}>
                                {formatNumber(repoTokens)}
                              </Typography>
                            </div>
                            {/* Requests */}
                            <div style={{ width: '70px', textAlign: 'center' }}>
                              <Typography style={{ fontSize: '13px', fontWeight: 400, color: '#667085' }}>
                                {formatNumber(getRequestCount(stat))}
                              </Typography>
                            </div>
                            {/* Chevron arrow */}
                            <div style={{ width: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="2">
                                <path d="M9 18l6-6-6-6" />
                              </svg>
                            </div>
                          </div>
                        );
                      })}
                  </>
                )}

                {/* Bottom Action Bar */}
                <div
                  style={{
                    height: '56px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    gap: '12px',
                    padding: '0 16px',
                    borderTop: '1px solid #E5E7EB',
                    backgroundColor: 'white',
                    marginTop: 'auto',
                  }}
                >
                  <Button
                    variant="contained"
                    size="small"
                    style={{
                      height: '36px',
                      borderRadius: '8px',
                      backgroundColor: '#617BFF',
                      color: 'white',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '13px',
                      padding: '8px 16px',
                    }}
                    onClick={() => navigateToTab(TabIndex.StorageRepos, '/repositories/inventory/add')}
                  >
                    Add
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    style={{
                      height: '36px',
                      borderRadius: '8px',
                      border: '1px solid #D0D5DD',
                      backgroundColor: 'white',
                      color: '#344054',
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '14px',
                      padding: '8px 16px',
                    }}
                    onClick={() => navigateToTab(TabIndex.StorageRepos, '/repositories/inventory')}
                  >
                    View all
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Third Row - Request Rate Chart + Explore Our Guides (50/50) */}
        <div className="flex flex-col md:flex-row gap-6 mb-8">
          {/* Request Rate Chart (30 days) - Left 50% */}
          <div className="relative" style={{ flex: 1, minWidth: 0 }}>
            {/* Carte grise de fond */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{ height: '317px', position: 'absolute', zIndex: 1 }}
            ></div>

            {/* Header */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}
              >
                Request Rate
              </Typography>
              <Typography
                style={{ color: '#535862', fontWeight: 400, fontSize: '13px', lineHeight: '20px' }}
              >
                {formatNumber(requestRateData.reduce((sum, d) => sum + d.requests, 0))} requests
              </Typography>
            </div>

            {/* Carte blanche */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
              style={{
                height: '275px',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div style={{ width: '100%', height: '100%' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={requestRateData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                    <defs>
                      <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#617BFF" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#617BFF" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" vertical={true} />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10, fill: '#6B7280' }} 
                      tickLine={false}
                      axisLine={{ stroke: '#E5E7EB' }}
                      tickFormatter={(v, i) => (i === 0 || i === 5 || i === 10 || i === 15 || i === 20 || i === 25 || i === 29) ? v : ''}
                      interval={0}
                    />
                    <YAxis 
                      tick={{ fontSize: 10, fill: '#6B7280' }} 
                      tickLine={false}
                      axisLine={false}
                      tickFormatter={(value) => formatNumber(value)}
                      width={40}
                      domain={[0, (() => {
                        const maxVal = Math.max(...requestRateData.map(d => d.requests), 0);
                        if (maxVal === 0) return 1000;
                        const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
                        return Math.ceil(maxVal / magnitude) * magnitude;
                      })()]}
                      tickCount={6}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #E5E7EB', 
                        borderRadius: '8px',
                        fontSize: '12px'
                      }}
                      formatter={(value: number) => [formatNumber(value), 'Requests']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="requests" 
                      stroke="#617BFF" 
                      strokeWidth={2}
                      fillOpacity={1} 
                      fill="url(#colorRequests)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>

          {/* Explore Our Guides Card - Right 50% */}
          <div className="relative" style={{ flex: 1, minWidth: 0 }}>
            {/* Carte grise de fond */}
            <div
              className="w-full bg-[#F8F9FA] rounded-xl border border-gray-200"
              style={{ height: '317px', position: 'absolute', zIndex: 1 }}
            ></div>

            {/* Header */}
            <div
              style={{
                height: '40px',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '16px',
                paddingRight: '16px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <Typography
                variant="h6"
                style={{ color: '#181D27', fontWeight: 600, fontSize: '14px', lineHeight: '20px' }}
              >
                Explore Our Guides
              </Typography>
            </div>

            {/* Carte blanche */}
            <div
              className="w-full bg-white border border-gray-200 shadow-sm flex flex-col"
              style={{
                height: '275px',
                borderRadius: '12px',
                padding: '16px',
                marginTop: '2px',
                position: 'relative',
                zIndex: 2,
              }}
            >
              <div
                style={{
                  maxHeight: 240,
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                <div className="flex space-x-4" style={{ paddingBottom: '4px' }}>
                  {/* Guide 1: Create a Repository */}
                  <div
                    style={{
                      width: 180,
                      height: 235,
                      minWidth: '180px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                        Create a Repository
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                        Learn how to create a new storage repository
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/start-with-cloud-storage-1/create-a-repository', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View guide <span style={{ fontSize: '14px' }}>→</span>
                    </div>
                  </div>

                  {/* Guide 2: Configure a Repository */}
                  <div
                    style={{
                      width: 180,
                      height: 235,
                      minWidth: '180px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                        Configure a Repository
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                        Set up and configure your repository settings
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/start-with-cloud-storage-1/program-with-flashback', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View guide <span style={{ fontSize: '14px' }}>→</span>
                    </div>
                  </div>

                  {/* Guide 3: Store an Object */}
                  <div
                    style={{
                      width: 180,
                      height: 235,
                      minWidth: '180px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                        Store an Object
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                        Upload and store objects in your repository
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/test-a-repository/program-with-flashback', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View guide <span style={{ fontSize: '14px' }}>→</span>
                    </div>
                  </div>

                  {/* Guide 4: Send a Prompt */}
                  <div
                    style={{
                      width: 180,
                      height: 235,
                      minWidth: '180px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                        Send a Prompt
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                        Send AI prompts through your repository
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/guides/setup-the-cloud-and-ai-gateway/test-a-repository/program-with-flashback-1', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View guide <span style={{ fontSize: '14px' }}>→</span>
                    </div>
                  </div>

                  {/* Guide 5: Repository API Reference */}
                  <div
                    style={{
                      width: 180,
                      height: 235,
                      minWidth: '180px',
                      borderRadius: '12px',
                      border: '1px solid #e5e7eb',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      padding: '16px',
                      backgroundColor: 'white',
                    }}
                  >
                    <div>
                      <Typography style={{ fontWeight: 600, fontSize: '14px', color: '#181D27', lineHeight: '20px', marginBottom: '6px' }}>
                        Repository API Reference
                      </Typography>
                      <Typography style={{ fontSize: '12px', color: '#6B7280', lineHeight: '16px' }}>
                        Explore the complete Repository API documentation
                      </Typography>
                    </div>
                    <div
                      onClick={() => window.open('https://docs.flashback.tech/support-reference/platform-api-reference/repo-management', '_blank')}
                      style={{ color: '#4766FF', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      View docs <span style={{ fontSize: '14px' }}>→</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </div>

      {/* Create Repository Modal */}
      <Modal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        aria-labelledby="create-repo-modal"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 480,
            bgcolor: 'white',
            borderRadius: '16px',
            boxShadow: '0px 20px 24px -4px rgba(16, 24, 40, 0.08), 0px 8px 8px -4px rgba(16, 24, 40, 0.03)',
            outline: 'none',
          }}
        >
          {/* Modal Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              borderBottom: '1px solid #E5E7EB',
            }}
          >
            <Box>
              <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#181D27' }}>
                Create Repository
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#535862', mt: 0.5 }}>
                Add a new storage repository to your workspace
              </Typography>
            </Box>
            <IconButton
              onClick={() => setCreateModalOpen(false)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: '8px',
                border: '1px solid #E5E7EB',
                '&:hover': { bgcolor: '#F9FAFB' },
              }}
            >
              <CloseIcon sx={{ fontSize: 20, color: '#667085' }} />
            </IconButton>
          </Box>

          {/* Modal Body */}
          <Box sx={{ p: 3 }}>
            {/* Repository Name */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 1 }}>
                Repository Name
              </Typography>
              <TextField
                fullWidth
                placeholder="Enter repository name"
                value={newRepoName}
                onChange={(e) => setNewRepoName(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    '& fieldset': { borderColor: '#D0D5DD' },
                    '&:hover fieldset': { borderColor: '#617BFF' },
                    '&.Mui-focused fieldset': { borderColor: '#617BFF' },
                  },
                  '& .MuiInputBase-input': {
                    padding: '12px 14px',
                    fontSize: '14px',
                  },
                }}
              />
            </Box>

            {/* Storage Provider */}
            <Box sx={{ mb: 3 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#344054', mb: 1 }}>
                Storage Provider
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={newRepoProvider}
                  onChange={(e) => setNewRepoProvider(e.target.value)}
                  sx={{
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#D0D5DD' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#617BFF' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#617BFF' },
                    '& .MuiSelect-select': {
                      padding: '12px 14px',
                      fontSize: '14px',
                    },
                  }}
                >
                  <MenuItem value="S3">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '4px', bgcolor: '#F5F7FF', border: '0.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={awsLogo} alt="AWS S3" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                      </Box>
                      <Typography>Amazon S3</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="GCS">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '4px', bgcolor: '#F5F7FF', border: '0.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={googleLogo} alt="Google Cloud Storage" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                      </Box>
                      <Typography>Google Cloud Storage</Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="BLOB">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ width: 24, height: 24, borderRadius: '4px', bgcolor: '#F5F7FF', border: '0.5px solid #E5E7EB', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={azureLogo} alt="Azure Blob Storage" style={{ width: 16, height: 16, objectFit: 'contain' }} />
                      </Box>
                      <Typography>Azure Blob Storage</Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* Info Box */}
            <Box
              sx={{
                p: 2,
                bgcolor: '#F0F2FF',
                borderRadius: '8px',
                border: '1px solid #D6DDFF',
              }}
            >
              <Typography sx={{ fontSize: '13px', color: '#4A5568', lineHeight: 1.5 }}>
                After creating the repository, you'll be able to configure buckets, API keys, and access permissions.
              </Typography>
            </Box>
          </Box>

          {/* Modal Footer */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 1.5,
              p: 3,
              borderTop: '1px solid #E5E7EB',
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setCreateModalOpen(false);
                setNewRepoName('');
                setNewRepoProvider('S3');
              }}
              sx={{
                height: 40,
                px: 2,
                borderRadius: '8px',
                border: '1px solid #D0D5DD',
                color: '#344054',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                '&:hover': {
                  bgcolor: '#F9FAFB',
                  borderColor: '#D0D5DD',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              disabled={!newRepoName.trim() || isCreating}
              onClick={() => {
                // Navigate to AddRepo page with pre-filled data
                setCreateModalOpen(false);
                navigateToTab(TabIndex.StorageRepos, '/repositories/add');
              }}
              sx={{
                height: 40,
                px: 2,
                borderRadius: '8px',
                bgcolor: '#617BFF',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '14px',
                '&:hover': {
                  bgcolor: '#4A67E3',
                },
                '&.Mui-disabled': {
                  bgcolor: '#E5E7EB',
                  color: '#9CA3AF',
                },
              }}
            >
              {isCreating ? 'Creating...' : 'Create Repository'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default RepoOverview;
